'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { loadBrushLottie } from '@/lib/brush-lottie';

// Only `/` blocks scroll. Flags are reset in the non-blocking branch — fine while
// this list stays a singleton. If another blocking route is added, also reset at
// the start of the blocking branch (or on pathname change), or a stale `true`
// from a prior visit can skip waitForEvent.
const PATH_WITH_SCROLL_BLOCK = ['/'];
const HERO_READY_TIMEOUT_MS = 2500;
const BRUSH_READY_TIMEOUT_MS = 10_000;
const READY_DELAY_MS = 100;
const DESKTOP_MEDIA_QUERY = '(min-width: 992px)';
export const HERO_READY_EVENT = 'qream:hero-ready';
export const BRUSH_READY_EVENT = 'qream:brush-ready';
let heroReadyFlag = false;
let brushReadyFlag = false;
export const dispatchHeroReady = () => {
  heroReadyFlag = true;
  window.dispatchEvent(new Event(HERO_READY_EVENT));
};
export const dispatchBrushReady = () => {
  brushReadyFlag = true;
  window.dispatchEvent(new Event(BRUSH_READY_EVENT));
};
const resetReadyFlags = () => {
  heroReadyFlag = false;
  brushReadyFlag = false;
};
const waitForEvent = (isReady: () => boolean, event: string, timeoutMs: number) => {
  const cancellers: Array<() => void> = [];
  const promise = new Promise<void>(resolve => {
    if (isReady()) {
      resolve();
      return;
    }
    const finish = () => {
      window.removeEventListener(event, finish);
      window.clearTimeout(timeoutId);
      resolve();
    };
    window.addEventListener(event, finish);
    const timeoutId = window.setTimeout(finish, timeoutMs);
    cancellers.push(finish);
  });
  return {
    promise,
    cancel: () => {
      for (const cancel of cancellers) cancel();
    }
  };
};
const useLoader = () => {
  const $ = _c(9);
  const pathname = usePathname();
  const [isPageReady, setIsPageReady] = useState(false);
  let t0;
  if ($[0] !== pathname) {
    t0 = PATH_WITH_SCROLL_BLOCK.includes(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const shouldBlockScroll = t0;
  const enableScrolling = _temp;
  let t1;
  if ($[2] !== shouldBlockScroll) {
    t1 = () => {
      if (!shouldBlockScroll) {
        resetReadyFlags();
        enableScrolling();
        setIsPageReady(true);
        return;
      }
      setIsPageReady(false);
      let cancelled = false;
      let didMark = false;
      let delayId;
      const waits = [];
      const markReady = () => {
        if (cancelled || didMark) {
          return;
        }
        didMark = true;
        delayId = window.setTimeout(() => {
          if (cancelled) {
            return;
          }
          enableScrolling();
          setIsPageReady(true);
        }, READY_DELAY_MS);
      };
      const needsBrush = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
      if (needsBrush) {
        loadBrushLottie();
      }
      const waitUntilReady = async () => {
        const hero = waitForEvent(_temp2, HERO_READY_EVENT, HERO_READY_TIMEOUT_MS);
        waits.push(hero);
        await hero.promise;
        if (cancelled) {
          return;
        }
        if (needsBrush) {
          const brush = waitForEvent(_temp3, BRUSH_READY_EVENT, BRUSH_READY_TIMEOUT_MS);
          waits.push(brush);
          await brush.promise;
          if (cancelled) {
            return;
          }
        }
        markReady();
      };
      waitUntilReady();
      return () => {
        cancelled = true;
        for (const wait of waits) {
          wait.cancel();
        }
        window.clearTimeout(delayId);
      };
    };
    $[2] = shouldBlockScroll;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== pathname || $[5] !== shouldBlockScroll) {
    t2 = [pathname, shouldBlockScroll];
    $[4] = pathname;
    $[5] = shouldBlockScroll;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== isPageReady) {
    t3 = {
      isPageReady
    };
    $[7] = isPageReady;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export default useLoader;
function _temp() {
  document.body.style.overflow = "initial";
  document.body.style.position = "initial";
}
function _temp2() {
  return heroReadyFlag;
}
function _temp3() {
  return brushReadyFlag;
}